import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";

/**
 * AdminGrants — one-click UI on top of POST /api/admin/practitioners/grant-free.
 *
 * Use case: comping a brand partner, giving a founder friend a free year,
 * customer-service overrides. Idempotent — if the email already exists,
 * we upgrade role → PROVIDER + refresh the grant, keeping their data.
 *
 * All the risky logic (password hashing, provider profile bootstrap,
 * subscription grant timestamp) lives in the backend; this is just a form.
 */
export default function AdminGrants() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [days, setDays] = useState(365);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error("Email, password, and name are required");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/practitioners/grant-free", {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        businessName: businessName.trim() || null,
        days: Number(days) || 365,
      });
      setResult(data);
      toast.success(data.created ? "Practitioner account created" : "Existing account upgraded + grant refreshed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed — check the console");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="admin-grants-panel">
      <div>
        <div className="label-mystic text-secondary">Free Practitioner Grants</div>
        <h2 className="font-serif text-3xl mt-2">Comp a Practitioner Account</h2>
        <p className="text-foreground/60 text-sm mt-2 leading-relaxed">
          Grant free Practitioner access (subscription bypasses Stripe). Use for brand partners,
          Founders, giveaways, or customer-service overrides. If the email already exists, we upgrade
          role → PROVIDER and refresh the grant window — no data lost.
        </p>
      </div>

      <form onSubmit={submit} className="parchment p-6 space-y-4">
        <Field label="Email" required data-testid="grant-email-field">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-input border border-secondary/25 rounded-sm p-3"
            placeholder="user@example.com"
            data-testid="grant-email-input"
          />
        </Field>

        <Field label="Password" required hint="Sent to user out-of-band (email, phone, etc.)">
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-input border border-secondary/25 rounded-sm p-3 font-mono"
            placeholder="Min 8 characters"
            data-testid="grant-password-input"
          />
        </Field>

        <Field label="Display Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-input border border-secondary/25 rounded-sm p-3"
            placeholder="How they're known publicly"
            data-testid="grant-name-input"
          />
        </Field>

        <Field label="Business Name" hint="Defaults to the display name if left empty">
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            className="w-full bg-input border border-secondary/25 rounded-sm p-3"
            placeholder="Optional"
            data-testid="grant-business-input"
          />
        </Field>

        <Field label="Grant Length (days)" hint="1 year = 365. Range 1–3650.">
          <input
            type="number"
            min={1}
            max={3650}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-40 bg-input border border-secondary/25 rounded-sm p-3"
            data-testid="grant-days-input"
          />
        </Field>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-60 py-3 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold inline-flex items-center justify-center gap-2 transition"
          data-testid="grant-submit-btn"
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Granting…</>
          ) : (
            <><Gift className="w-4 h-4" /> Grant Free Practitioner Access</>
          )}
        </button>
      </form>

      {result && (
        <div className="parchment p-5 space-y-2" data-testid="grant-result">
          <div className="flex items-center gap-2 text-secondary">
            <CheckCircle2 className="w-4 h-4" />
            <span className="label-mystic">
              {result.created ? "Account created" : "Account upgraded"}
            </span>
          </div>
          <div className="text-sm space-y-1 text-foreground/80">
            <div><span className="text-foreground/55">Email:</span> {result.email}</div>
            <div><span className="text-foreground/55">Role:</span> {result.role}</div>
            <div><span className="text-foreground/55">Grant expires:</span> {new Date(result.expiresAt).toLocaleDateString()} ({result.days} days)</div>
            <div><span className="text-foreground/55">User ID:</span> <code className="text-[11px]">{result.userId}</code></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="label-mystic text-foreground/70 block mb-1.5">
        {label}{required && <span className="text-secondary"> *</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}
